import React, { useEffect, useState } from "react";
import config from "../config";
import McpSharingFields from "./McpSharingFields";
import "./EditMcpServerModal.css";

/**
 * Edit an existing global MCP server. Pre-filled from the row's current values
 * so a single field (e.g. a mistyped scope) can be fixed without retyping
 * everything. PUTs to /admin/mcp-servers/:id.
 *
 * The OAuth client secret is never returned by the API (only
 * `has_oauth_client_secret`), so the secret field starts blank — leaving it
 * blank keeps the stored secret (the backend only overwrites it when a
 * non-empty value is sent). Render only when `server` is set, and key by
 * `server._id` so each open starts from fresh values.
 */
export default function EditMcpServerModal({ server, onClose, onSaved }) {
  const [values, setValues] = useState({
    name: server.name || "",
    url: server.url || "",
    description: server.description || "",
    enabled: server.enabled !== false,
    oauth_client_id: server.oauth_client_id || "",
    oauth_scopes: server.oauth_scopes || "",
    oauth_authorize_endpoint: server.oauth_authorize_endpoint || "",
    oauth_token_endpoint: server.oauth_token_endpoint || "",
    shared_with_everyone: server.shared_with_everyone !== false,
    shared_with_users: server.shared_with_users || [],
    shared_with_roles: server.shared_with_roles || [],
  });
  const [secret, setSecret] = useState("");
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Esc closes the modal (unless a save is in flight).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !saving) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, saving]);

  const onField = (e) => {
    const { name, value, type, checked } = e.target;
    setValues((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!values.name.trim() || !values.url.trim()) {
      setError("MCP name and URL are required.");
      return;
    }
    setSaving(true);
    try {
      // Only send the secret when the admin typed a new one — an empty value
      // would otherwise be a no-op server-side, but omitting it is clearer.
      const payload = { ...values };
      if (secret.trim()) payload.oauth_client_secret = secret;
      const response = await fetch(
        `${config.api.baseUrl}/admin/mcp-servers/${server._id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(payload),
        }
      );
      const data = await response.json();
      if (data.success) {
        onSaved();
      } else {
        setError(data.error || "Failed to update MCP server");
      }
    } catch (err) {
      setError("An error occurred while updating the MCP server.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="mcp-edit-overlay"
      onClick={() => { if (!saving) onClose(); }}
    >
      <div
        className="mcp-edit-modal"
        role="dialog"
        aria-modal="true"
        aria-label={`Edit MCP server: ${server.name}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mcp-edit-header">
          <h3 className="mcp-edit-title">Edit MCP server: {server.name}</h3>
          <button
            type="button"
            className="mcp-edit-close"
            onClick={onClose}
            disabled={saving}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={onSubmit} className="admin-form mcp-edit-body">
          <div className="form-group">
            <label htmlFor="edit_mcp_name">
              Name <span className="required">*</span>
            </label>
            <input
              id="edit_mcp_name"
              name="name"
              value={values.name}
              onChange={onField}
              placeholder="e.g., Miro"
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="edit_mcp_url">
              URL <span className="required">*</span>
            </label>
            <input
              id="edit_mcp_url"
              name="url"
              value={values.url}
              onChange={onField}
              placeholder="https://..."
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="edit_mcp_description">Description</label>
            <textarea
              id="edit_mcp_description"
              name="description"
              rows={3}
              value={values.description}
              onChange={onField}
              placeholder="Optional description shown in UI"
            />
          </div>
          <div className="form-group-checkbox">
            <input
              type="checkbox"
              id="edit_mcp_enabled"
              name="enabled"
              checked={values.enabled}
              onChange={onField}
            />
            <label htmlFor="edit_mcp_enabled">Enabled</label>
          </div>

          <McpSharingFields idPrefix="edit_mcp" value={values} onChange={setValues} />

          <div className="form-group">
            <label htmlFor="edit_mcp_oauth_client_id">OAuth Client ID</label>
            <input
              id="edit_mcp_oauth_client_id"
              name="oauth_client_id"
              value={values.oauth_client_id}
              onChange={onField}
              placeholder="From your provider's app settings"
            />
          </div>
          <div className="form-group">
            <label htmlFor="edit_mcp_oauth_client_secret">OAuth Client Secret</label>
            <input
              id="edit_mcp_oauth_client_secret"
              name="oauth_client_secret"
              type="password"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              placeholder={
                server.has_oauth_client_secret
                  ? "A secret is set — leave blank to keep it"
                  : "No secret set"
              }
            />
            <div className="mcp-edit-help">
              {server.has_oauth_client_secret
                ? "Leave blank to keep the current secret; type a new value to replace it."
                : "No secret is stored. Enter one only if this provider requires it."}
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="edit_mcp_oauth_scopes">OAuth Scopes (optional)</label>
            <input
              id="edit_mcp_oauth_scopes"
              name="oauth_scopes"
              value={values.oauth_scopes}
              onChange={onField}
              placeholder="Space-separated; blank = auto-discover"
            />
          </div>
          <div className="form-group">
            <label htmlFor="edit_mcp_oauth_authorize_endpoint">
              Authorize Endpoint override (optional)
            </label>
            <input
              id="edit_mcp_oauth_authorize_endpoint"
              name="oauth_authorize_endpoint"
              value={values.oauth_authorize_endpoint}
              onChange={onField}
              placeholder="Leave blank to use discovery"
            />
          </div>
          <div className="form-group">
            <label htmlFor="edit_mcp_oauth_token_endpoint">
              Token Endpoint override (optional)
            </label>
            <input
              id="edit_mcp_oauth_token_endpoint"
              name="oauth_token_endpoint"
              value={values.oauth_token_endpoint}
              onChange={onField}
              placeholder="Leave blank to use discovery"
            />
          </div>

          <div className="mcp-edit-footer">
            <button
              type="button"
              className="admin-submit-button mcp-edit-cancel"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button type="submit" className="admin-submit-button" disabled={saving}>
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
